import { Center } from "@astryxdesign/core/Center";
import { EmptyState } from "@astryxdesign/core/EmptyState";
import { Section } from "@astryxdesign/core/Section";
import { Spinner } from "@astryxdesign/core/Spinner";
import { HStack, VStack } from "@astryxdesign/core/Stack";
import { Text } from "@astryxdesign/core/Text";
import { useToast } from "@astryxdesign/core/Toast";
import { Navigate, useNavigate, useSearch } from "@tanstack/react-router";
import { useMemo } from "react";
import type { DueEntry } from "../../shared/money";
import { AppFrame } from "../components/AppFrame";
import { AddSub } from "../components/money/AddSub";
import { DueRow } from "../components/money/DueRow";
import { MoneySummary } from "../components/money/MoneySummary";
import { SubDialog } from "../components/money/SubDialog";
import { SubRow } from "../components/money/SubRow";
import { ApiError } from "../lib/api";
import { WalletIcon } from "../lib/icons";
import { useMoney, usePayCharge } from "../lib/money-queries";

export function MoneyPage() {
  const navigate = useNavigate();
  const search = useSearch({ from: "/money" });
  const { data, error, isPending } = useMoney();
  const pay = usePayCharge();
  const toast = useToast();

  const { recurring, installments } = useMemo(() => {
    const active = (data?.subs ?? []).filter((s) => s.cancelledAt === null);
    return {
      recurring: active
        .filter((s) => s.kind === "recurring")
        .sort((a, b) => Number(a.paused) - Number(b.paused) || a.name.localeCompare(b.name)),
      installments: active
        .filter((s) => s.kind === "installment")
        .sort((a, b) => a.firstDate.localeCompare(b.firstDate)),
    };
  }, [data?.subs]);

  if (error instanceof ApiError && error.status === 401) return <Navigate to="/login" />;

  const openSub = (id: string) =>
    void navigate({ to: "/money", search: { sub: id } });
  const closeSub = () => void navigate({ to: "/money", search: {}, replace: true });

  const onPay = (entry: DueEntry) => {
    pay.mutate(
      { id: entry.subId, dueDate: entry.dueDate },
      {
        onSuccess: () =>
          toast({
            body: `Paid ${entry.name}`,
            uniqueID: "sub-paid",
            endContent: (
              <button
                type="button"
                onClick={() =>
                  pay.mutate({ id: entry.subId, dueDate: entry.dueDate, undo: true })
                }
                style={{
                  background: "none",
                  border: "none",
                  color: "inherit",
                  font: "inherit",
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
              >
                Undo
              </button>
            ),
          }),
      },
    );
  };

  const selected = search.sub ? (data?.subs.find((s) => s.id === search.sub) ?? null) : null;

  return (
    <AppFrame active="money" dueCount={data?.summary.dueSoonCount ?? 0}>
      <Center axis="horizontal">
        <VStack gap={3} maxWidth={760} width="100%" padding={4}>
          <AddSub />

          {isPending ? (
            <Center minHeight={240}>
              <Spinner size="lg" label="Loading" />
            </Center>
          ) : !data ? null : data.subs.length === 0 ? (
            <Center minHeight={240}>
              <EmptyState
                icon={<WalletIcon fontSize={40} />}
                title="No subscriptions yet"
                description="Add the ones that keep charging you — Netflix, iCloud, an installment plan."
              />
            </Center>
          ) : (
            <>
              <MoneySummary summary={data.summary} fx={data.fx} />

              {data.due.length > 0 ? (
                <VStack gap={1}>
                  <HStack gap={1} align="center">
                    <Text type="label" color="secondary">
                      Due
                    </Text>
                    {data.summary.overdueCount > 0 ? (
                      <Text type="supporting" size="xsm" color="accent">
                        {data.summary.overdueCount} overdue
                      </Text>
                    ) : null}
                  </HStack>
                  <Section padding={0}>
                    <VStack as="ul" gap={0} paddingBlock={1}>
                      {data.due.map((entry) => (
                        <DueRow
                          key={`${entry.subId}-${entry.dueDate}`}
                          entry={entry}
                          onPay={onPay}
                          onOpen={openSub}
                          isPaying={pay.isPending}
                        />
                      ))}
                    </VStack>
                  </Section>
                </VStack>
              ) : null}

              {installments.length > 0 ? (
                <VStack gap={1}>
                  <Text type="label" color="secondary">
                    Installments
                  </Text>
                  <Section padding={0}>
                    <VStack as="ul" gap={0} paddingBlock={1}>
                      {installments.map((sub) => (
                        <SubRow
                          key={sub.id}
                          sub={sub}
                          due={data.due}
                          payments={data.payments}
                          onOpen={openSub}
                        />
                      ))}
                    </VStack>
                  </Section>
                </VStack>
              ) : null}

              {recurring.length > 0 ? (
                <VStack gap={1}>
                  <Text type="label" color="secondary">
                    Subscriptions
                  </Text>
                  <Section padding={0}>
                    <VStack as="ul" gap={0} paddingBlock={1}>
                      {recurring.map((sub) => (
                        <SubRow
                          key={sub.id}
                          sub={sub}
                          due={data.due}
                          payments={data.payments}
                          onOpen={openSub}
                        />
                      ))}
                    </VStack>
                  </Section>
                </VStack>
              ) : null}
            </>
          )}
        </VStack>
      </Center>
      {selected && data ? (
        <SubDialog key={selected.id} sub={selected} state={data} onClose={closeSub} />
      ) : null}
    </AppFrame>
  );
}
